'use client';

import {useEffect, useRef, useState} from 'react';

import {useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {Checkbox} from '@udemy-v2/react-form-components';

import {YOUTUBE_TERMS_URL} from './external-video-consent';

import styles from './consent-gate.module.scss';

interface ConsentGateProps {
    onAccept: (rememberChoice: boolean) => void;
    onDismiss: () => void;
}

export function ConsentGate({onAccept, onDismiss}: ConsentGateProps) {
    const {gettext} = useI18n();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [rememberChoice, setRememberChoice] = useState(false);

    // Move focus to the CTA when the overlay appears.
    useEffect(() => {
        buttonRef.current?.focus();
    }, []);

    // Dismiss on Escape without starting playback.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onDismiss();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onDismiss]);

    return (
        <div
            className={styles['overlay']}
            role="dialog"
            aria-modal="true"
            aria-label={gettext('External content consent')}
        >
            <div className={styles['panel']}>
                <div className={styles['message-row']}>
                    <Icon
                        className={styles['message-icon']}
                        glyph="error"
                        label={false}
                        size="medium"
                        color="inherit"
                    />
                    <p className={styles['disclaimer']}>
                        {gettext(
                            'This content is hosted by a third party. By showing the external content you accept the ',
                        )}
                        <a
                            className={styles['terms-link']}
                            href={YOUTUBE_TERMS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {gettext('terms and conditions')}
                        </a>
                        {gettext(' of youtube-nocookie.com.')}
                    </p>
                </div>

                <button
                    ref={buttonRef}
                    type="button"
                    className={styles['cta-button']}
                    onClick={() => onAccept(rememberChoice)}
                >
                    <span className={styles['play-circle']} aria-hidden="true">
                        <Icon glyph="play-arrow" label={false} size="small" color="inherit" />
                    </span>
                    <span className={styles['cta-label']}>{gettext('Show external content')}</span>
                </button>

                <div className={styles['remember-section']}>
                    <Checkbox
                        className={styles['remember-checkbox']}
                        size="small"
                        checked={
                            rememberChoice ? Checkbox.STATE.CHECKED : Checkbox.STATE.UNCHECKED
                        }
                        onChange={(e) => setRememberChoice(e.target.checked)}
                    >
                        {gettext('Remember my choice')}
                    </Checkbox>
                    <p className={styles['remember-helper']}>
                        {gettext(
                            "Your choice will be remembered until you close this browser.",
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}
